/**
 * Column labels for the bot table's unique-URL count and its requests-per-URL ratio, DERIVED FROM
 * the metadata the backend puts on the analytics response as `semantics.bot_unique_urls`
 * (PR #5924, task-5v28el4jeg).
 *
 * Today the backend returns each bot's unique_urls as the LARGEST CHUNK's distinct count per day, a
 * lower bound (the capture agent resets its per-bot URL set per chunk, so a day's rows are disjoint
 * chunk counts and no table holds the union), summed across the window. Two disjoint chunks of 100
 * URLs with 200 requests display 100 URLs and 2.0x requests per URL although every URL was
 * requested once, so the ratio is an UPPER bound. When the agent keeps the set cumulative per day
 * (task-sl8e2nlcpo) the backend flips `bound` and `label` and this file needs no change: the
 * header text is the backend's label, and the ratio is qualified only while `bound` is 'lower'.
 *
 * A response without the metadata comes from a backend older than #5924; that case keeps the
 * legacy labels and reports `qualified: false`. An UNKNOWN bound throws: a new value must be read
 * on purpose, never displayed under the old words.
 */

export interface BotUniqueUrlsSemantics {
  per_date: string;
  window: string;
  bound: string;
  label: string;
  description: string;
}

export interface BotUniqueUrlsColumns {
  qualified: boolean;
  header: string;
  title?: string;
  ratioHeader: string;
  ratioTitle?: string;
}

const RATIO_UPPER_BOUND_TITLE =
  'Requests divided by the unique-URL count shown, which is a LOWER bound on unique URLs, so this '
  + 'ratio is an UPPER bound: two disjoint chunks of 100 URLs with 200 requests show 100 URLs and '
  + '2.0x although every URL was requested once.';

export function botUniqueUrlsColumns(semantics: BotUniqueUrlsSemantics | undefined): BotUniqueUrlsColumns {
  if (!semantics) {
    return { qualified: false, header: 'Unique URLs', ratioHeader: 'Req/URL' };
  }
  const header = `Unique URLs (${semantics.label})`;
  switch (semantics.bound) {
    case 'lower':
      return {
        qualified: true,
        header,
        title: semantics.description,
        ratioHeader: 'Req/URL (upper bound)',
        ratioTitle: RATIO_UPPER_BOUND_TITLE,
      };
    case 'exact':
      return { qualified: true, header, title: semantics.description, ratioHeader: 'Req/URL' };
    case 'estimate':
      // every value is a day-scope HyperLogLog estimate (task-sl8e2nlcpo): the ratio inherits the
      // estimate's error and is neither an upper nor a lower bound
      return {
        qualified: true,
        header,
        title: semantics.description,
        ratioHeader: 'Req/URL (≈)',
        ratioTitle: 'Requests divided by an estimate of unique URLs (HyperLogLog sketch); the ratio '
          + "inherits the estimate's relative error.",
      };
    case 'lower_estimate':
      // every value is a sketch estimate but at least one day carries one run's estimate, a lower
      // bound on the day: an ESTIMATE of a lower bound is not a bound, so the ratio claims neither
      return {
        qualified: true,
        header,
        title: semantics.description,
        ratioHeader: 'Req/URL (≲)',
        ratioTitle: 'Requests divided by an estimate of a lower bound on unique URLs: about at most this, not a '
          + 'guaranteed upper bound, since the sketch can overshoot the count it estimates.',
      };
    case 'mixed':
      // chunk-peak rows beside sketch rows: the peak rows make the ratio an upper bound, and each
      // value is marked in its own cell
      return {
        qualified: true,
        header,
        title: semantics.description,
        ratioHeader: 'Req/URL (see marks)',
        ratioTitle: 'Some values in this window are chunk peaks or capped counts (a lower bound on unique '
          + 'URLs, so their ratio is an upper bound) and some are estimates (their ratio is neither '
          + 'bound); each ratio is marked in its own cell.',
      };
    default:
      throw new Error(
        `Unknown bot unique_urls bound on the wire: ${semantics.bound} (label=${semantics.label}). `
        + 'The dashboard qualifies only lower, exact, estimate, lower_estimate and mixed; read the new one on purpose.',
      );
  }
}
